import { useEffect, useState } from "react";
import { KpiReportResponse, getKpiReport } from "../../api/kpi";
import {
  ActivityDashboardResponse,
  getActivityDashboard,
} from "../../api/analyticsAdmin";
import { completeMonthRange } from "../../helpers/dashboardMetrics";
import { DASHBOARD_REQUEST_TIMEOUT_MS } from "./dashboardRequest";

export interface MonthlyActivity {
  previous: ActivityDashboardResponse;
  latest: ActivityDashboardResponse;
}

export interface DashboardData {
  kpi: KpiReportResponse | null;
  activity: MonthlyActivity | null;
  loading: boolean;
}

// Which sources the admin may see: the KPI report needs KPIReport and the
// activity figures need Analytics. Nothing else is requested.
export interface DashboardSources {
  kpi: boolean;
  activity: boolean;
}

// Everything the landing page shows is fetched here and rendered in one go.
// Blocks used to load independently, which meant the stat grid re-ranked itself
// when the activity figures landed a moment after the KPI report — tiles visibly
// changing identity under the reader. Both sources are cached server-side, so
// waiting for both costs nothing worth the reshuffle.
//
// Both requests are month-scoped and carry a timeout: whatever fails or takes
// too long leaves its own block empty rather than holding the page.
export const useDashboardData = ({
  kpi: wantKpi,
  activity: wantActivity,
}: DashboardSources): DashboardData => {
  const [data, setData] = useState<Omit<DashboardData, "loading">>({
    kpi: null,
    activity: null,
  });
  const [loading, setLoading] = useState(wantKpi || wantActivity);

  useEffect(() => {
    if (!wantKpi && !wantActivity) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    const today = new Date();
    setLoading(true);

    const activityFor = (
      monthsBack: number
    ): Promise<ActivityDashboardResponse | null> => {
      if (!wantActivity) return Promise.resolve(null);
      const range = completeMonthRange(today, monthsBack);
      return getActivityDashboard(
        {
          from: range.from,
          to: range.to,
          platform: "All",
          audience: "All",
          granularity: "Month",
        },
        { timeout: DASHBOARD_REQUEST_TIMEOUT_MS }
      );
    };

    const kpiReport: Promise<KpiReportResponse | null> = wantKpi
      ? getKpiReport({ timeout: DASHBOARD_REQUEST_TIMEOUT_MS })
      : Promise.resolve(null);

    Promise.allSettled([kpiReport, activityFor(2), activityFor(1)]).then(
      ([kpiResult, previousResult, latestResult]) => {
        if (cancelled) return;
        const previous =
          previousResult.status === "fulfilled" ? previousResult.value : null;
        const latest =
          latestResult.status === "fulfilled" ? latestResult.value : null;
        setData({
          kpi: kpiResult.status === "fulfilled" ? kpiResult.value : null,
          activity: previous && latest ? { previous, latest } : null,
        });
        setLoading(false);
      }
    );

    return () => {
      cancelled = true;
    };
  }, [wantKpi, wantActivity]);

  return { ...data, loading };
};
